import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ToastType } from '../../components/audience/ToastType';
import { IEmail, IEmailTemplate, IValidationErrorCollection } from '../../models';
import { IApplicationState } from '../../store';
import { ActionCreators as AudienceDetailsActionCreators } from '../../store/AudienceDetailsStore';
import { ActionCreators as EmailActionCreators } from '../../store/EmailStore';
import * as EmailTemplateActions from '../../store/EmailTemplateSlice';
import { ActionCreators as SurveyActionCreators } from '../../store/SurveyStore';

export const useAudienceEmails = (audienceId: number, surveyId: number) => {
	const dispatch = useDispatch();

	const [isTemplateWarningModalOpen, setIsTemplateWarningModalOpen] = useState(false);
	const [selectedDefaultTemplate, setSelectedDefaultTemplate] = useState<{ template?: IEmailTemplate }>({ template: undefined });
	const { reminderEmailColumns } = useSelector((state: IApplicationState) => state.audienceDetails);
	const { sendEmailModalOpen } = useSelector((state: IApplicationState) => state.email);
	const { defaultEmailTemplates, invitationEmail, invitationEmailValidationErrors, reminderEmails, reminderEmailValidationErrors } =
		useSelector((state: IApplicationState) => state.emailTemplates);
	const { supportedCultures, survey } = useSelector((state: IApplicationState) => state.survey.surveyEditModel);
	React.useEffect(() => {
		if (audienceId !== 0) {
			dispatch(EmailTemplateActions.getEmailTemplates({ surveyId: surveyId, audienceId: audienceId }));
		}

		if (survey.surveyId === 0) {
			dispatch(SurveyActionCreators.GetSurvey(surveyId));
		}

		dispatch(EmailTemplateActions.getDefaultEmailTemplateList());
	}, [dispatch, surveyId, survey, audienceId]);

	const isMultiLanguageSurvey = supportedCultures && supportedCultures.length > 1;

	const AddInvitationEmailValidationErrors = (validationError?: IValidationErrorCollection) => {
		dispatch(EmailTemplateActions.addInvitationEmailValidationErrors(validationError));
	};
	const AddReminderToList = (emailTemplate: IEmailTemplate) => {
		dispatch(EmailTemplateActions.addReminderToList({ emailTemplate }));
	};
	const AddReminderEmailValidationErrors = (validationError?: IValidationErrorCollection) => {
		dispatch(EmailTemplateActions.addReminderEmailValidationErrors(validationError));
	};
	const DeleteReminder = (emailTemplate: IEmailTemplate) => {
		dispatch(EmailTemplateActions.deleteReminder(emailTemplate));
	};
	const UpdateInvitationEmail = (emailTemplate: IEmailTemplate) => {
		dispatch(EmailTemplateActions.updateInvitationEmail({ invitationEmail: emailTemplate }));
	};
	const Toast = (type: ToastType, message: string) => {
		dispatch(AudienceDetailsActionCreators.Toast(type, message));
	};
	const SendEmail = (emailTemplate: IEmail) => {
		dispatch(EmailActionCreators.SendEmail(emailTemplate));
	};
	const ToggleSendEmailModal = (isOpen: boolean) => {
		dispatch(EmailActionCreators.ToggleSendEmailModal(isOpen));
	};

	const isStringEmpty = (data?: string) => {
		return data === null || data === undefined || data.trim() === '';
	};
	const doesInvitationEmailHaveData = !(
		isStringEmpty(invitationEmail.from) &&
		isStringEmpty(invitationEmail.replyToEmail) &&
		isStringEmpty(invitationEmail.subject) &&
		isStringEmpty(invitationEmail.content)
	);
	const SelectDefaultTemplate = (selectedId: string) => {
		const selectedTemplate = defaultEmailTemplates.find((t) => t.emailTemplateId + '' === selectedId);
		if (selectedTemplate !== undefined) {
			setSelectedDefaultTemplate({ template: selectedTemplate });
			if (doesInvitationEmailHaveData) {
				setIsTemplateWarningModalOpen(true);
			} else {
				UpdateInvitationEmail({
					...invitationEmail,
					from: selectedTemplate.from,
					replyToEmail: selectedTemplate.replyToEmail,
					subject: selectedTemplate.subject,
					content: selectedTemplate.content,
					enableHtmlContent: selectedTemplate.enableHtmlContent,
				});
			}
		}
	};
	const SubmitTemplateWarningModal = () => {
		if (selectedDefaultTemplate.template !== undefined) {
			UpdateInvitationEmail({
				...invitationEmail,
				from: selectedDefaultTemplate.template.from,
				replyToEmail: selectedDefaultTemplate.template.replyToEmail,
				subject: selectedDefaultTemplate.template.subject,
				content: selectedDefaultTemplate.template.content,
				enableHtmlContent: selectedDefaultTemplate.template.enableHtmlContent,
			});
		}
		setIsTemplateWarningModalOpen(false);
	};
	const CloseTemplateWarningModal = () => {
		setIsTemplateWarningModalOpen(false);
	};

	return {
		defaultEmailTemplates,
		invitationEmail,
		invitationEmailValidationErrors,
		isMultiLanguageSurvey,
		isTemplateWarningModalOpen,
		reminderEmails,
		reminderEmailColumns,
		reminderEmailValidationErrors,
		selectedDefaultTemplate: selectedDefaultTemplate?.template,
		sendEmailModalOpen,
		AddInvitationEmailValidationErrors,
		AddReminderToList,
		AddReminderEmailValidationErrors,
		CloseTemplateWarningModal,
		DeleteReminder,
		SelectDefaultTemplate,
		SendEmail,
		SubmitTemplateWarningModal,
		Toast,
		ToggleSendEmailModal,
		UpdateInvitationEmail,
	};
};
